import { WEEKDAYS_SHORT } from '../events/calendar';
import { plusDays } from './summary';

/**
 * The period switch of "Podsumowanie" (owner 2026-10-30 22:49, option 1): „Ten tydzień · Ten miesiąc · Wszystkie”.
 * Days are Warsaw days ('YYYY-MM-DD', `todayInWarsaw`); the arithmetic is on the labels, never the browser's zone.
 * An entry's date is its `dueDay` (the event's day, the deadline, the day it came…). Before today it is overdue: shown
 * in every option, on top, in red. No date: shown in every option, at the end („Bez daty”).
 */
export const PERIODS = [
  { value: 'tydzien', label: 'Ten tydzień' },
  { value: 'miesiac', label: 'Ten miesiąc' },
  { value: 'wszystkie', label: 'Wszystkie' },
] as const;
export type Period = (typeof PERIODS)[number]['value'];
export const DEFAULT_PERIOD: Period = 'wszystkie';

/** Monday..Sunday of the week of `today`, or its calendar month; „Wszystkie”: no end (null). */
export function periodRange(period: Period, today: string): { from: string; to: string } | null {
  if (period === 'wszystkie') return null;
  if (period === 'tydzien') {
    const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7; // Monday 0 .. Sunday 6
    const from = plusDays(today, -weekday);
    return { from, to: plusDays(from, 6) };
  }
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${today.slice(0, 7)}-01`, to: `${today.slice(0, 7)}-${String(last).padStart(2, '0')}` };
}

/** A day from today on that the option shows (an earlier day is overdue, shown apart). */
export function inPeriod(day: string, period: Period, today: string): boolean {
  if (day < today) return false;
  const range = periodRange(period, today);
  return range === null || day <= range.to;
}

/** „Pon 2.11”; another year: „Pon 4.01.2027”. */
export function dayHeader(day: string, today: string): string {
  const weekday = WEEKDAYS_SHORT[(new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7];
  const text = `${weekday} ${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;
  return day.slice(0, 4) === today.slice(0, 4) ? text : `${text}.${day.slice(0, 4)}`;
}

export type EntryGroup<E> = { key: string; tone: 'overdue' | 'day' | 'none'; label: string; entries: E[] };

/**
 * What the option shows, in groups: „Zaległe” (the longest waiting first), then a group per day from today on (the
 * nearest first), then „Bez daty”. Entries of one day keep the order they came in (the most urgent first). No empty group.
 */
export function groupEntries<E extends { dueDay: string | null }>(entries: readonly E[], period: Period, today: string): EntryGroup<E>[] {
  const byDay = (a: E, b: E) => (a.dueDay! < b.dueDay! ? -1 : a.dueDay! > b.dueDay! ? 1 : 0); // Array.sort is stable
  const overdue = entries.filter((e) => e.dueDay !== null && e.dueDay < today).sort(byDay);
  const dated = entries.filter((e) => e.dueDay !== null && inPeriod(e.dueDay, period, today)).sort(byDay);
  const undated = entries.filter((e) => e.dueDay === null);
  const groups: EntryGroup<E>[] = [];
  if (overdue.length > 0) groups.push({ key: 'zalegle', tone: 'overdue', label: 'Zaległe', entries: overdue });
  for (const e of dated) {
    const last = groups[groups.length - 1];
    if (last?.tone === 'day' && last.key === e.dueDay) last.entries.push(e);
    else groups.push({ key: e.dueDay!, tone: 'day', label: dayHeader(e.dueDay!, today), entries: [e] });
  }
  if (undated.length > 0) groups.push({ key: 'bez-daty', tone: 'none', label: 'Bez daty', entries: undated });
  return groups;
}

/** How many entries each option shows (the overdue and the undated count in each). */
export function periodCounts(entries: readonly { dueDay: string | null }[], today: string): Record<Period, number> {
  const count = (period: Period) => entries.filter((e) => e.dueDay === null || e.dueDay < today || inPeriod(e.dueDay, period, today)).length;
  return { tydzien: count('tydzien'), miesiac: count('miesiac'), wszystkie: count('wszystkie') };
}

const periodKey = (userId: string) => `todo.summaryPeriod.${userId}`;

/** The user's last choice in this browser; nobody known, storage unavailable (private mode) or unknown value: „Wszystkie”. */
export function storedPeriod(userId: string | undefined): Period {
  if (!userId) return DEFAULT_PERIOD;
  try {
    const v = localStorage.getItem(periodKey(userId));
    return PERIODS.some((p) => p.value === v) ? (v as Period) : DEFAULT_PERIOD;
  } catch {
    return DEFAULT_PERIOD;
  }
}
export function storePeriod(userId: string | undefined, period: Period) {
  if (!userId) return;
  try {
    localStorage.setItem(periodKey(userId), period);
  } catch {
    // not remembered: the choice still works for this visit
  }
}
